/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TFunction } from 'i18next';
import { useState } from 'react';
import U2000InputMemo from '../../../../u2000Core/components/formControls/input/U2000InputMemo';
import U2000Label from '../../../../u2000Core/components/formControls/label/U2000Label';
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS, U3203ROLES } from '../../../U3203';
import type { U3203AiMissionFormRow, U3203InitLU, U3203TemplateVariableDefinition } from '../../../U3203Service';
import { useU3203Context } from '../../../U3203Context';
import U2000Tab from '../../../../u2000Core/components/tab/U2000Tab';
import U3203AiMissionHistoryTab from '../../history/U3203AiMissionHistoryTab';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import { u2000CanManageScopeAwareEntity } from '../../../../u2000Core/tools/U2000SystemScopeAccess';

interface U3203AiMissionPromptsTabProps {
  formStateHandlers: U2000FormStateHandlers<U3203AiMissionFormRow>;
  t: TFunction;
  onMissionRestored?: (row: U3203AiMissionFormRow) => void;
}

function U3203TemplateVariablesPanel({ label, initLU }: { label: string; initLU: U3203InitLU }) {
  const defs = initLU.templateVariableDefinitions ?? [];
  if (defs.length === 0) return null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2.5 [&_label]:mb-0">
      <U2000Label label={label} htmlFor="u3203-template-variables-panel" />
      <div
        id="u3203-template-variables-panel"
        className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-300 bg-gray-50 px-4 py-3"
      >
        <ul className="space-y-3 text-sm text-gray-700">
          {defs.map((def: U3203TemplateVariableDefinition) => (
            <li key={def.name} className="flex flex-col gap-1">
              <code className="w-fit rounded-sm bg-white px-2 py-0.5 font-mono text-xs text-blue-800">{def.placeholder}</code>
              <span className="text-gray-600">{def.description}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function U3203AiMissionPromptsTab({ formStateHandlers, t, onMissionRestored }: U3203AiMissionPromptsTabProps) {
  const { initLU } = useU3203Context();
  const { auth, hasRole } = useU2000AuthContext();
  const data = formStateHandlers.getData();
  const formEditMode = formStateHandlers.getFormEditMode();
  const canEditMission = u2000CanManageScopeAwareEntity({
    isSystemScope: data?.systemScope === true,
    hasRequiredRole: hasRole(formEditMode === U2000FormStateEnumEditMode.ADD ? U3203ROLES.ADD : U3203ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ || !canEditMission;
  const isEdit = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT;
  const [subTab, setSubTab] = useState<string>('editor');

  const templateVariableDefinitions = initLU.templateVariableDefinitions ?? [];
  const hasTemplateVariables = templateVariableDefinitions.length > 0;

  const templateVariablesLabel = t('lblTemplateVariablesHelp', { ns: NS });

  const editorContent = (
    <div className="space-y-6">
      <div
        className={
          hasTemplateVariables
            ? 'grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]'
            : ''
        }
      >
        <div className="flex flex-col gap-6">
          <U2000InputMemo
            formStateHandlers={formStateHandlers as any}
            fieldName="systemPrompt"
            label={t('lblSystemPrompt', { ns: NS })}
            required
            readonly={readonly}
            rows={hasTemplateVariables ? 12 : 14}
            minHeight={hasTemplateVariables ? '14rem' : '18rem'}
          />
          <U2000InputMemo
            formStateHandlers={formStateHandlers as any}
            fieldName="userPromptTemplate"
            label={t('lblUserPromptTemplate', { ns: NS })}
            readonly={readonly}
            rows={hasTemplateVariables ? 6 : 8}
            minHeight={hasTemplateVariables ? '8rem' : '10rem'}
          />
        </div>
        {hasTemplateVariables ? (
          <U3203TemplateVariablesPanel label={templateVariablesLabel} initLU={initLU} />
        ) : null}
      </div>
      {isEdit && canEditMission ? (
        <U2000InputMemo
          formStateHandlers={formStateHandlers as any}
          fieldName="revisionComment"
          label={t('lblRevisionComment', { ns: NS })}
          readonly={false}
          placeholder={t('lblRevisionCommentPlaceholder', { ns: NS })}
          rows={2}
          minHeight="4rem"
        />
      ) : null}
    </div>
  );

  const showVersions = Boolean(data?.id && hasRole(U3203ROLES.READ));

  const subTabs = [
    { id: 'editor', label: t('tabPromptsEditor', { ns: NS }), content: editorContent },
    showVersions
      ? {
          id: 'versions',
          label: t('tabVersions', { ns: NS }),
          content: (
            <U3203AiMissionHistoryTab
              missionId={data!.id}
              systemScope={data!.systemScope}
              version={data!.version}
              onMissionRestored={(row) => {
                formStateHandlers.setData(row);
                onMissionRestored?.(row);
              }}
            />
          ),
        }
      : null,
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  if (subTabs.length === 1) {
    return editorContent;
  }

  return (
    <U2000Tab
      tabs={subTabs}
      defaultTab="editor"
      activeTab={subTab}
      setActiveTab={(tab) => setSubTab(typeof tab === 'string' ? tab : String(tab))}
    />
  );
}

export default U3203AiMissionPromptsTab;
